if (!customElements.get('variant-card-picker')) {
  customElements.define(
    'variant-card-picker',
    class VariantCardPicker extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleStoqUpdate = this.handleStoqUpdate.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.fitCardTitles = this.fitCardTitles.bind(this);
      }

      connectedCallback() {
        this.variantSelects = this.closest('variant-selects');
        this.variantData = this.getVariantData();
        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('stoq:loaded', this.handleStoqUpdate);
        document.addEventListener('stoq:product-changed', this.handleStoqUpdate);
        window.addEventListener('load', this.handleStoqUpdate);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        this.enhanceDropdowns();
        this.titleResizeObserver = new ResizeObserver(this.fitCardTitles);
        this.titleResizeObserver.observe(this);
        this.updateDisplay();
        requestAnimationFrame(() => this.handleStoqUpdate());
        this.fitCardTitles();

        if (document.fonts?.ready) document.fonts.ready.then(this.fitCardTitles);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('change', this.handleChange);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('stoq:loaded', this.handleStoqUpdate);
        document.removeEventListener('stoq:product-changed', this.handleStoqUpdate);
        window.removeEventListener('load', this.handleStoqUpdate);
        this.variantChangeUnsubscriber?.();
        this.closeDropdowns();
        this.titleResizeObserver?.disconnect();
      }

      fitCardTitles() {
        this.querySelectorAll('.product-form__input--cards').forEach((fieldset) => {
          const titles = [...fieldset.querySelectorAll('.variant-card-picker__value')];
          if (!titles.length) return;

          fieldset.style.removeProperty('--variant-card-picker-card-text-size');

          const defaultFontSize = parseFloat(getComputedStyle(titles[0]).fontSize);
          const minimumFontSize = Math.max(7, defaultFontSize * 0.7);
          let fontSize = defaultFontSize;
          const titlesFit = () => titles.every((title) => {
            return title.scrollHeight <= title.clientHeight + 1 && title.scrollWidth <= title.clientWidth + 1;
          });

          while (!titlesFit() && fontSize > minimumFontSize) {
            fontSize = Math.max(minimumFontSize, fontSize - 0.5);
            fieldset.style.setProperty('--variant-card-picker-card-text-size', `${fontSize}px`);
          }
        });
      }

      handleChange() {
        this.updateDisplay();

        const selectedVariant = this.findVariant(this.getSelectedOptions());
        if (selectedVariant) this.updateMainPrice(selectedVariant);
      }

      handleStoqUpdate() {
        const latestVariantData = this.getVariantData();

        if (latestVariantData.length) this.variantData = latestVariantData;

        this.updateDisplay();
      }

      handleVariantChange(event) {
        if (event.data.sectionId !== this.variantSelects?.dataset.section) return;

        const updatedPicker = event.data.html.getElementById(this.variantSelects.id);
        if (!updatedPicker) return;

        this.querySelectorAll('.variant-card-picker__item').forEach((item) => {
          const inputId = item.querySelector('input[data-card-option]')?.id;
          const updatedItem = inputId && updatedPicker.querySelector(`[id="${CSS.escape(inputId)}"]`)?.closest('.variant-card-picker__item');
          if (!updatedItem) return;

          const currentBadge = item.querySelector('[data-availability-badge]');
          const updatedBadge = updatedItem.querySelector('[data-availability-badge]');

          if (currentBadge && updatedBadge) currentBadge.replaceWith(updatedBadge.cloneNode(true));
          else if (currentBadge) currentBadge.remove();
          else if (updatedBadge) item.querySelector('.variant-card-picker__card')?.prepend(updatedBadge.cloneNode(true));
        });

        document.dispatchEvent(new Event('product-card-availability:refresh'));
      }

      handleClick(event) {
        const dropdownToggle = event.target.closest('[data-dropdown-toggle]');
        if (dropdownToggle && this.contains(dropdownToggle)) {
          event.preventDefault();
          this.toggleDropdown(dropdownToggle.closest('.variant-picker-dropdown'));
          return;
        }

        const dropdownOption = event.target.closest('[data-dropdown-option]');
        if (dropdownOption && this.contains(dropdownOption)) {
          event.preventDefault();
          this.selectDropdownOption(dropdownOption);
          return;
        }

        const card = event.target.closest('.variant-card-picker__card');

        if (!card || !this.contains(card)) return;

        const input = document.getElementById(card.htmlFor);

        if (!input || input.checked) return;

        event.preventDefault();
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      handleDocumentClick(event) {
        const clickedWrapper = event.target.closest?.('.variant-picker-dropdown');

        this.querySelectorAll('.variant-picker-dropdown.is-open').forEach((wrapper) => {
          if (wrapper !== clickedWrapper) this.closeDropdown(wrapper);
        });
      }

      handleKeydown(event) {
        const toggle = event.target.closest?.('[data-dropdown-toggle]');
        const option = event.target.closest?.('[data-dropdown-option]');

        if (!toggle && !option) return;

        const wrapper = (toggle || option).closest('.variant-picker-dropdown');
        if (!wrapper) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDropdown(wrapper);
          wrapper.querySelector('[data-dropdown-toggle]')?.focus();
          return;
        }

        if (toggle && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          this.openDropdown(wrapper);
          wrapper.querySelector('[data-dropdown-option][aria-selected="true"]')?.focus();
          return;
        }

        if (!option) return;

        const options = [...wrapper.querySelectorAll('[data-dropdown-option]')];
        const currentIndex = options.indexOf(option);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowDown') nextIndex = Math.min(options.length - 1, currentIndex + 1);
        if (event.key === 'ArrowUp') nextIndex = Math.max(0, currentIndex - 1);
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = options.length - 1;

        if (nextIndex !== currentIndex) {
          event.preventDefault();
          options[nextIndex]?.focus();
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectDropdownOption(option);
        }
      }

      enhanceDropdowns() {
        this.querySelectorAll('.variant-picker-dropdown').forEach((wrapper) => {
          const customPicker = wrapper.querySelector('[data-dropdown-custom]');
          if (!customPicker) return;

          wrapper.classList.add('is-enhanced');
          customPicker.hidden = false;
        });
      }

      toggleDropdown(wrapper) {
        if (!wrapper) return;

        if (wrapper.classList.contains('is-open')) {
          this.closeDropdown(wrapper);
        } else {
          this.openDropdown(wrapper);
        }
      }

      openDropdown(wrapper) {
        if (!wrapper) return;

        this.querySelectorAll('.variant-picker-dropdown.is-open').forEach((openWrapper) => {
          if (openWrapper !== wrapper) this.closeDropdown(openWrapper);
        });

        wrapper.classList.add('is-open');
        wrapper.querySelector('[data-dropdown-toggle]')?.setAttribute('aria-expanded', 'true');
        const options = wrapper.querySelector('[data-dropdown-options]');
        if (options) options.hidden = false;
      }

      closeDropdown(wrapper) {
        if (!wrapper) return;

        wrapper.classList.remove('is-open');
        wrapper.querySelector('[data-dropdown-toggle]')?.setAttribute('aria-expanded', 'false');
        const options = wrapper.querySelector('[data-dropdown-options]');
        if (options) options.hidden = true;
      }

      closeDropdowns() {
        this.querySelectorAll('.variant-picker-dropdown').forEach((wrapper) => this.closeDropdown(wrapper));
      }

      selectDropdownOption(option) {
        const wrapper = option.closest('.variant-picker-dropdown');
        const select = wrapper?.querySelector('[data-variant-dropdown-select]');
        if (!wrapper || !select) return;

        if (select.value !== option.dataset.value) {
          select.value = option.dataset.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          this.updateDisplay();
        }

        this.closeDropdown(wrapper);
        wrapper.querySelector('[data-dropdown-toggle]')?.focus();
      }

      getVariantData() {
        const dataElement = this.variantSelects?.querySelector('script[type="application/json"]');

        if (!dataElement) return [];

        try {
          return JSON.parse(dataElement.textContent);
        } catch (error) {
          console.error('Unable to parse variant-card product data.', error);
          return [];
        }
      }

      getSelectedOptions() {
        return Array.from(this.variantSelects?.querySelectorAll('select, fieldset') || [], (element) => {
          if (element.tagName === 'SELECT') return element.value;

          return element.querySelector('input[type="radio"]:checked')?.value;
        });
      }

      findVariant(options) {
        return this.variantData.find((variant) => {
          return variant.options.every((option, index) => option === options[index]);
        });
      }

      updateDisplay() {
        const selectedOptions = this.getSelectedOptions();

        this.querySelectorAll('.product-form__input--cards[data-option-position]').forEach((fieldset) => {
          const selectedInput = fieldset.querySelector('input[type="radio"]:checked');
          const selectedValue = fieldset.querySelector('[data-selected-card-value]');

          if (selectedInput && selectedValue) selectedValue.textContent = selectedInput.value;

          this.updatePrices(fieldset, selectedOptions);
        });

        this.querySelectorAll('.product-form__input--swatch').forEach((fieldset) => {
          const selectedInput = fieldset.querySelector('input[type="radio"]:checked');
          const selectedValue = fieldset.querySelector('[data-selected-swatch-value]');

          if (selectedInput && selectedValue) selectedValue.textContent = selectedInput.value;
        });

        this.querySelectorAll('.variant-picker-dropdown').forEach((wrapper) => {
          const select = wrapper.querySelector('select');
          const selectedValue = wrapper.querySelector('[data-selected-dropdown-value]');

          if (select && selectedValue && select.selectedOptions.length) {
            const selectedOption = wrapper.querySelector(
              `[data-dropdown-option][data-value="${CSS.escape(select.value)}"]`
            );
            selectedValue.textContent = selectedOption?.dataset.name || select.value;
          }

          this.updateDropdown(wrapper, selectedOptions);
        });
      }

      updateDropdown(wrapper, selectedOptions) {
        const select = wrapper.querySelector('[data-variant-dropdown-select]');
        const selectedRow = wrapper.querySelector(
          `[data-dropdown-option][data-value="${CSS.escape(select?.value || '')}"]`
        );
        const optionIndex = Number(wrapper.dataset.optionPosition) - 1;

        wrapper.querySelectorAll('[data-dropdown-option]').forEach((row) => {
          const variant = this.findDropdownVariant(selectedOptions, optionIndex, row.dataset.value);
          const availability = variant
            ? this.getAvailabilityState(variant)
            : row.dataset.availability || 'in-stock';
          const badge = row.querySelector('[data-dropdown-badge]');
          const price = row.querySelector('[data-dropdown-price]');
          const customNote = row.dataset.note?.trim();
          let badgeLabel = '';
          let badgeType = '';

          if (customNote) {
            badgeLabel = customNote;
            badgeType = 'custom';
          } else if (availability === 'preorder') {
            badgeLabel = wrapper.dataset.labelPreorder;
            badgeType = availability;
          } else if (availability === 'out-of-stock') {
            badgeLabel = wrapper.dataset.labelOutOfStock;
            badgeType = availability;
          }

          row.dataset.availability = availability;
          row.setAttribute('aria-selected', String(row === selectedRow));
          row.tabIndex = row === selectedRow ? 0 : -1;

          if (price && price.dataset.staticPrice !== 'true') {
            price.textContent = variant ? this.formatMoney(variant.price) : '';
          }

          if (badge) {
            badge.dataset.badgeType = badgeType;
            badge.textContent = badgeLabel;
            badge.hidden = badgeLabel === '';
          }
        });

        const selectedName = wrapper.querySelector('[data-dropdown-selected-name]');

        if (selectedName) selectedName.textContent = selectedRow?.dataset.name || select?.value || '';
      }

      findDropdownVariant(selectedOptions, optionIndex, value) {
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = value;
        return this.findVariant(candidateOptions);
      }

      getAvailabilityState(variant) {
        if (!variant || !variant.available) return 'out-of-stock';
        if (this.hasStoqPreorder(variant.id)) return 'preorder';

        if (
          variant.inventory_management === 'shopify'
          && variant.inventory_policy === 'continue'
          && Number(variant.inventory_quantity) <= 0
        ) {
          return 'preorder';
        }

        return 'in-stock';
      }

      hasStoqPreorder(variantId) {
        const getSellingPlan = window._RestockRocket?.getSellingPlan;
        const numericVariantId = Number(variantId);

        if (typeof getSellingPlan !== 'function' || !Number.isSafeInteger(numericVariantId)) return false;

        try {
          return Boolean(getSellingPlan(numericVariantId));
        } catch (error) {
          console.error('Unable to read the STOQ selling plan for a variant dropdown.', error);
          return false;
        }
      }

      updateMainPrice(variant) {
        const sectionId = this.variantSelects?.dataset.section;
        const priceRoot = document.getElementById(`price-${sectionId}`);
        if (!priceRoot) return;

        const suggestedAddOns = document.getElementById(`SuggestedAddOns-${sectionId}`);
        const additionalProducts = document.getElementById(`AdditionalProducts-${sectionId}`);
        if (suggestedAddOns?.updateBaseVariant) {
          suggestedAddOns.updateBaseVariant(variant);
        }
        if (additionalProducts?.updateBaseVariant) additionalProducts.updateBaseVariant(variant);
        if (suggestedAddOns?.updateBaseVariant || additionalProducts?.updateBaseVariant) return;

        const price = priceRoot.querySelector('.price');
        if (!price) return;

        const formattedPrice = this.formatMoney(variant.price);
        const salePrice = price.querySelector('.price__sale .price-item--sale');
        const regularPrice = price.querySelector('.price__regular .price-item--regular');
        const comparePrice = price.querySelector('.price__compare-price .price-item--regular');

        if (price.classList.contains('price--on-sale') && salePrice) {
          salePrice.textContent = formattedPrice;
          if (comparePrice) comparePrice.textContent = this.formatMoney(variant.compare_at_price || 0);
        } else if (regularPrice) {
          regularPrice.textContent = formattedPrice;
        }

        const vatPrice = priceRoot.querySelector('.product-price-vat');
        if (vatPrice) vatPrice.textContent = `${this.formatMoney(Math.round(variant.price * 5 / 6))} EXCL VAT`;
      }

      updatePrices(fieldset, selectedOptions) {
        const baselineInput = fieldset.querySelector('[data-price-baseline="true"]');
        const optionIndex = Number(fieldset.dataset.optionPosition) - 1;
        let baselineVariant = null;

        if (baselineInput) {
          const baselineOptions = [...selectedOptions];
          baselineOptions[optionIndex] = baselineInput.value;
          baselineVariant = this.findVariant(baselineOptions);
        }

        if (baselineInput && !baselineVariant) return;

        fieldset.querySelectorAll('[data-card-option]').forEach((input) => {
          const priceElement = input.closest('.variant-card-picker__item')?.querySelector('[data-card-price]');

          if (!priceElement || priceElement.dataset.staticPrice === 'true') return;

          const candidateOptions = [...selectedOptions];
          candidateOptions[optionIndex] = input.value;
          const candidateVariant = this.findVariant(candidateOptions);

          if (!candidateVariant) {
            priceElement.textContent = '';
            return;
          }

          if (!baselineVariant) {
            priceElement.textContent = this.formatMoney(candidateVariant.price);
            return;
          }

          if (input === baselineInput) {
            priceElement.textContent = this.formatMoney(0);
            return;
          }

          const difference = candidateVariant.price - baselineVariant.price;
          priceElement.textContent = difference > 0 ? `+${this.formatMoney(difference)}` : '';
        });
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{ amount }}';
        const placeholder = format.match(/{{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*}}/);

        if (!placeholder) return (cents / 100).toFixed(2);

        const formatType = placeholder[1];
        const useDecimals = !formatType.includes('no_decimals');
        const useCommaSeparator = formatType.includes('comma_separator');
        const amount = (cents / 100).toLocaleString(useCommaSeparator ? 'de-DE' : 'en-GB', {
          minimumFractionDigits: useDecimals ? 2 : 0,
          maximumFractionDigits: useDecimals ? 2 : 0,
        });

        return format.replace(placeholder[0], amount);
      }
    }
  );
}
